type SeoShape = {
  title?: string;
  description?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  canonicalUrl?: string;
  metaTitle?: string;
  metaDescription?: string;
  twitterTitle?: string;
  twitterDescription?: string;
};

type BaseNormalizedEntity = {
  id: string;
  baseSlug: string;
  slug: string;
  title: string;
  excerpt?: string;
  summary?: string;
  body?: string;
  seo?: SeoShape;
  localePublishState?: Record<string, { isPublished?: boolean; isIndexable?: boolean }>;
};

function resolveLocalizedValue(value: unknown, locale?: string): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value !== 'object') return String(value);

  const record = value as Record<string, unknown>;
  const candidate =
    (locale && record[locale]) ||
    record.en ||
    record.title ||
    record.name ||
    record.value ||
    record.label;

  if (candidate === null || candidate === undefined) return '';
  if (typeof candidate === 'string') return candidate;
  return String(candidate);
}

function resolveSourceEntity<T extends BaseNormalizedEntity & Record<string, any>>(entity: T): T {
  const envelopeData = entity?.data;
  const looksLikeEnvelope =
    envelopeData &&
    typeof envelopeData === 'object' &&
    !Array.isArray(envelopeData) &&
    ('locale' in entity || 'market' in entity || 'fallbackUsed' in entity || 'translationStatus' in entity || 'isIndexable' in entity);

  return (looksLikeEnvelope ? envelopeData : entity) as T;
}

function resolveLocalizedSlug(source: Record<string, unknown>, locale?: string): string {
  const localizedSlug = source.localizedSlug;

  if (localizedSlug && typeof localizedSlug === 'object' && !Array.isArray(localizedSlug)) {
    const resolved = resolveLocalizedValue(localizedSlug, locale);
    if (resolved) return resolved;
  }

  return resolveLocalizedValue(source.slug, locale) || String(source.baseSlug || source.slug || '');
}

function resolveLocalizedBlockData(block: Record<string, any>, locale?: string) {
  const data = block?.data;

  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return data;
  }

  if (locale && (data[locale] !== undefined || data.en !== undefined || data.title !== undefined || data.body !== undefined)) {
    return data[locale] ?? data.en ?? data;
  }

  return data;
}

function resolveContentBlocks(blocks: unknown, locale?: string) {
  if (!Array.isArray(blocks)) return blocks;

  return blocks.map((block) => {
    if (!block || typeof block !== 'object') return block;

    return {
      ...(block as Record<string, any>),
      data: resolveLocalizedBlockData(block as Record<string, any>, locale),
    };
  });
}

export function normalizeCmsEntity<T extends BaseNormalizedEntity>(entity: T, locale?: string): T {
  const source = resolveSourceEntity(entity as T & Record<string, any>);
  const resolvedTitle = resolveLocalizedValue(source.title, locale);
  const resolvedExcerpt = resolveLocalizedValue(source.excerpt, locale);
  const resolvedSummary = resolveLocalizedValue(source.summary, locale);
  const resolvedBody = resolveLocalizedValue(source.body, locale);
  const resolvedSlug = resolveLocalizedSlug(source, locale);
  const resolvedContentBlocks = resolveContentBlocks(source.contentBlocks, locale);

  return {
    ...entity,
    ...source,
    _id: (entity as T & { _id?: string; id?: string })._id || source._id || source.id || entity.id,
    slug: resolvedSlug,
    excerpt: resolvedExcerpt,
    summary: resolvedSummary,
    body: resolvedBody,
    contentBlocks: resolvedContentBlocks,
    title: resolvedTitle || resolveLocalizedValue((source as T & { name?: unknown }).name, locale) || '',
    seo: {
      title: resolveLocalizedValue(source.seo?.title, locale) || resolvedTitle,
      description:
        resolveLocalizedValue(source.seo?.description, locale) || resolvedExcerpt || resolvedSummary || '',
      ogTitle:
        resolveLocalizedValue(source.seo?.ogTitle, locale) ||
        resolveLocalizedValue(source.seo?.title, locale) ||
        resolvedTitle,
      ogDescription:
        resolveLocalizedValue(source.seo?.ogDescription, locale) ||
        resolveLocalizedValue(source.seo?.description, locale) ||
        resolvedExcerpt ||
        '',
      twitterTitle:
        resolveLocalizedValue(source.seo?.twitterTitle, locale) ||
        resolveLocalizedValue(source.seo?.ogTitle, locale) ||
        resolveLocalizedValue(source.seo?.title, locale) ||
        resolvedTitle,
      twitterDescription:
        resolveLocalizedValue(source.seo?.twitterDescription, locale) ||
        resolveLocalizedValue(source.seo?.ogDescription, locale) ||
        resolveLocalizedValue(source.seo?.description, locale) ||
        resolvedExcerpt ||
      '',
      ogImage: resolveLocalizedValue(source.seo?.ogImage, locale),
      canonicalUrl: resolveLocalizedValue(source.seo?.canonicalUrl, locale),
      metaTitle: resolveLocalizedValue(source.seo?.metaTitle, locale),
      metaDescription: resolveLocalizedValue(source.seo?.metaDescription, locale),
    },
  } as T;
}
